/**
 * SP Ship-Date Calculator Tracking with PostHog
 * ---------------------------------------------------------------------------
 * Instruments the "Calculate" button on the subscription join timeline
 * (sections/sp-sc-join-timeline.liquid, assets/sp-sc-date-calculator.js).
 *
 * WHY A PATH TRAIL IS RECORDED HERE AND NOT LEFT TO POSTHOG'S PATHS INSIGHT
 *
 * PostHog can already draw journeys from autocaptured $pageview events, and
 * its Paths insight is the right tool for open-ended exploration. It is a poor
 * fit for a metric you want to WATCH, for three reasons:
 *
 *   1. Paths is its own insight type. You cannot break a Trends line down by
 *      "the route people took", so "clicks over time" and "which route" end up
 *      in two charts that cannot be filtered against each other.
 *   2. Paths depends on $pageview autocapture staying on. If someone turns
 *      autocapture down to save events, the journey silently disappears while
 *      the click event keeps working, which reads as "people stopped arriving
 *      by that route" rather than "we stopped recording it".
 *   3. Path cleaning rules live in project settings, so the grouping can
 *      change under a saved insight without the insight changing.
 *
 * Recording the trail as a plain string PROPERTY on the click event makes
 * "which route did most people take" an ordinary breakdown on the same Trends
 * chart as the click count. That works on every PostHog plan tier, survives
 * autocapture settings, and is what makes this usable as a tracked metric
 * rather than an occasional exploration.
 *
 * The trail is first-party and anonymous: same-origin pathnames from
 * sessionStorage, no query strings (they carry emails and discount codes from
 * campaign links), no IDs, and it dies with the tab.
 */

(function () {
  'use strict';
  if (window.__spQA == null) window.__spQA = (function () { var q = /[?&]sp_qa=([01])(?:&|$)/.exec(location.search), s = false; try { if (q) localStorage.setItem('sp_qa', q[1]); s = localStorage.getItem('sp_qa') === '1'; } catch (e) {} var r = window.Shopify && window.Shopify.theme && window.Shopify.theme.role; return s || /^(localhost|127\.0\.0\.1)$/.test(location.hostname) || /SimplePaperQA/.test(navigator.userAgent) || (!!r && r !== 'main'); })();

  var TRAIL_KEY = 'sp_path_trail';
  var TRAIL_MAX = 8;          // last 8 pages; deeper trails fragment the breakdown
  var EVENT = 'sc_ship_date_calculated';

  /* ---------------------------------------------------------------- trail */

  function safeSession() {
    // Private windows and blocked site-data both throw on ACCESS, not on read,
    // so the whole accessor has to be inside the try.
    try {
      var s = window.sessionStorage;
      s.getItem(TRAIL_KEY);
      return s;
    } catch (e) {
      return null;
    }
  }

  function cleanPath(pathname) {
    if (!pathname) return '/';
    // Collapse the high-cardinality tails that would otherwise make every
    // trail unique and destroy the breakdown.
    return pathname
      .replace(/\/products\/[^/]+/, '/products/:handle')
      .replace(/\/collections\/[^/]+\/products\/[^/]+/, '/collections/:handle/products/:handle')
      .replace(/\/collections\/[^/]+/, '/collections/:handle')
      .replace(/\/blogs\/[^/]+\/[^/]+/, '/blogs/:blog/:article')
      .replace(/\/$/, '') || '/';
  }

  function readTrail(store) {
    if (!store) return [];
    try {
      var raw = JSON.parse(store.getItem(TRAIL_KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function recordThisPage(store) {
    if (!store) return [];
    var trail = readTrail(store);
    var here = cleanPath(window.location.pathname);
    // Do not record a reload or an in-page anchor as another step.
    if (trail[trail.length - 1] !== here) trail.push(here);
    if (trail.length > TRAIL_MAX) trail = trail.slice(-TRAIL_MAX);
    try {
      store.setItem(TRAIL_KEY, JSON.stringify(trail));
    } catch (e) { /* quota or blocked: the event still sends without a trail */ }
    return trail;
  }

  function externalReferrerHost() {
    try {
      if (!document.referrer) return 'direct';
      var host = new URL(document.referrer).hostname;
      return host === window.location.hostname ? 'internal' : host;
    } catch (e) {
      return 'unknown';
    }
  }

  /* -------------------------------------------------------------- posthog */

  function waitForPostHog(callback, maxAttempts) {
    var attempts = 0;
    maxAttempts = maxAttempts || 50;
    (function check() {
      attempts++;
      if (typeof window.posthog !== 'undefined' && typeof window.posthog.capture === 'function') {
        callback();
      } else if (attempts < maxAttempts) {
        setTimeout(check, 100);
      }
      // No warn on give-up: this section renders on one template, and a noisy
      // console on every other page is how tracking scripts get removed.
    })();
  }

  function initCalcTracking() {
    var forms = document.querySelectorAll('[data-sp-date-calculator]');
    if (!forms.length) return;

    var store = safeSession();
    var trail = recordThisPage(store);
    var calcCount = 0;

    forms.forEach(function (form) {
      // Listen in the CAPTURE phase. sp-sc-date-calculator.js calls
      // preventDefault in its own submit handler, and a listener attached
      // later in the bubble phase would still run, but capture makes the
      // ordering independent of which script loads first.
      form.addEventListener('submit', function () {
        var input = form.querySelector('[data-sp-date-calculator-input]');
        var value = input && input.value ? input.value : '';

        calcCount++;

        var props = {
          // ---- the metric's own dimensions
          path_trail: trail.slice(),                 // array, for drill-down
          path_trail_string: trail.join(' > '),      // scalar, for breakdowns
          entry_path: trail[0] || cleanPath(window.location.pathname),
          previous_path: trail.length > 1 ? trail[trail.length - 2] : null,
          steps_to_calculator: Math.max(trail.length - 1, 0),
          referrer_host: externalReferrerHost(),
          page_path: cleanPath(window.location.pathname),

          // ---- interaction shape
          calc_index_in_session: calcCount,          // 1 = first try, >1 = re-runs
          has_date: Boolean(value),                  // false = clicked with an empty field
          viewport_width: window.innerWidth
        };

        // The chosen date itself is the customer's input, so only its distance
        // from today is sent: that is the part that describes the decision,
        // and it cannot be joined back to a person the way a raw date can.
        if (value) {
          var picked = input.valueAsDate
            ? new Date(input.valueAsDate.getUTCFullYear(), input.valueAsDate.getUTCMonth(), input.valueAsDate.getUTCDate())
            : new Date(value);
          if (!isNaN(picked.getTime())) {
            var today = new Date();
            today.setHours(0, 0, 0, 0);
            props.days_from_today = Math.round((picked - today) / 86400000);
            props.picked_day_of_month = picked.getDate();
          }
        }

        try {
          if (!window.__spQA) window.posthog.capture(EVENT, props);
        } catch (e) { /* never let tracking break the calculator */ }
      }, true);
    });
  }

  function start() {
    waitForPostHog(initCalcTracking);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
